import React, { useState, useEffect } from "react";
import { User, ExamAssignment, AssessmentTemplate } from "../../types";
import { useApp } from "../../context/AppContext";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import ExamTakingPage from "./ExamTakingPage";
import { db } from "../../firebase/config";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";

interface Props { 
  user: User;
  onNavigate?: (tab: string) => void;
}

interface EnhancedAssignment extends ExamAssignment {
  examData?: AssessmentTemplate;
}

const MyExamsPage: React.FC<Props> = ({ user, onNavigate }) => {
  const { assignments: localAssignments } = useApp();
  const [taking, setTaking] = useState<{ assignment: EnhancedAssignment } | null>(null);
  const [enhancedAssignments, setEnhancedAssignments] = useState<EnhancedAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const assignmentsRef = collection(db, "exam_assignments");
        const q = query(assignmentsRef, where("candidateId", "==", user.id));
        const assignmentsSnapshot = await getDocs(q);

        const assignmentsData: EnhancedAssignment[] = [];

        for (const docSnapshot of assignmentsSnapshot.docs) {
          const data = docSnapshot.data();
          const assignmentData: ExamAssignment = {
            id: docSnapshot.id,
            examId: data.examId,
            candidateId: data.candidateId,
            orgId: data.orgId,
            status: data.status,
            assignedAt: data.assignedAt,
            scheduledStart: data.scheduledStart,
            scheduledEnd: data.scheduledEnd,
            startedAt: data.startedAt,
            completedAt: data.completedAt,
            score: data.score,
            maxScore: data.maxScore,
            timeSpent: data.timeSpent,
            createdBy: data.createdBy,
            notes: data.notes,
          };

          let examData: AssessmentTemplate | undefined = undefined;

          if (assignmentData.examId) {
            const assessmentRef = doc(db, "assessmentTemplates", assignmentData.examId);
            const assessmentSnapshot = await getDoc(assessmentRef);

            if (assessmentSnapshot.exists()) {
              const templateData = assessmentSnapshot.data();
              examData = {
                id: assessmentSnapshot.id,
                name: templateData.name,
                description: templateData.description,
                targetAudience: templateData.targetAudience,
                sections: templateData.sections,
                totalQuestions: templateData.totalQuestions,
                totalDuration: templateData.totalDuration,
                instructions: templateData.instructions,
                timeLimit: templateData.timeLimit,
                isActive: templateData.isActive,
                createdBy: templateData.createdBy,
                createdAt: templateData.createdAt,
                updatedAt: templateData.updatedAt
              };
            }
          }

          assignmentsData.push({
            ...assignmentData,
            examData
          });
        }

        setEnhancedAssignments(assignmentsData);
      } catch (error) {
        console.error("Error fetching assignments:", error);
        const localData = localAssignments
          .filter(a => a.candidateId === user.id)
          .map(a => ({
            ...a,
            examData: undefined
          }));
        setEnhancedAssignments(localData);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user.id, localAssignments]);

  const checkExamTiming = (scheduledStart: string | null | undefined, scheduledEnd: string | null | undefined) => {
    if (!scheduledStart || !scheduledEnd) {
      return { canStart: false, status: 'invalid', buttonText: 'Schedule Not Set', disabled: true };
    }

    const now = new Date();
    const startDate = new Date(scheduledStart);
    const endDate = new Date(scheduledEnd);

    if (now < startDate) {
      return {
        canStart: false,
        status: 'not_started',
        buttonText: `Starts ${startDate.toLocaleString()}`,
        disabled: true
      };
    } else if (now >= startDate && now <= endDate) {
      return {
        canStart: true,
        status: 'ongoing',
        buttonText: 'Start Exam →',
        disabled: false
      };
    } else {
      return {
        canStart: false,
        status: 'expired',
        buttonText: 'Expired',
        disabled: true
      };
    }
  };

  const getButtonConfig = (assignment: EnhancedAssignment) => {
    if (assignment.status === 'completed') {
      return { text: 'View Result', variant: 'secondary' as const, disabled: false, onClick: () => { } };
    }

    if (assignment.status === 'in_progress') {
      return {
        text: 'Resume Exam',
        variant: 'warning' as const,
        disabled: false,
        onClick: () => {
          localStorage.setItem("currentAssignmentId", assignment.id);
          if (onNavigate) {
            onNavigate("exam_start");
          }
        }
      };
    }

    const timing = checkExamTiming(assignment.scheduledStart, assignment.scheduledEnd);

    if (timing.status === 'expired') {
      return { text: 'Expired', variant: 'secondary' as const, disabled: true, onClick: () => { } };
    }

    if (timing.status === 'not_started') {
      return { text: timing.buttonText, variant: 'secondary' as const, disabled: true, onClick: () => { } };
    }

    if (timing.status === 'invalid') {
      return { text: 'Not Available', variant: 'secondary' as const, disabled: true, onClick: () => { } };
    }

    return {
      text: 'Start Exam',
      variant: 'primary' as const,
      disabled: false,
      onClick: () => {
        localStorage.setItem("currentAssignmentId", assignment.id);
        if (onNavigate) {
          onNavigate("exam_start");
        }
      }
    };
  };

  if (taking) {
    const exam = taking.assignment.examData;
    if (!exam) {
      return (
        <PageShell title="My Exams" subtitle="View and attempt your assigned assessments">
          <GlassCard className="p-16 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <p className="text-red-500 font-semibold text-lg">Exam data not found</p>
            <Button onClick={() => setTaking(null)} className="mt-4">Go Back</Button>
          </GlassCard>
        </PageShell>
      );
    }

    return (
      <ExamTakingPage
        assessmentTemplate={exam}
        assignment={taking.assignment}
        candidateId={user.id}
        onFinish={() => setTaking(null)}
      />
    );
  }

  if (loading) {
    return (
      <PageShell title="My Exams" subtitle="Loading your assessments...">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <GlassCard key={i} className="p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </GlassCard>
          ))}
        </div>
      </PageShell>
    );
  }

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not scheduled';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const pendingExams = enhancedAssignments.filter(a => a.status !== "completed");
  const completedExams = enhancedAssignments.filter(a => a.status === "completed");

  if (enhancedAssignments.length === 0) {
    return (
      <PageShell title="My Exams" subtitle="View and attempt your assigned assessments">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard label="Total" value={0} icon="📋" color="#6366f1" />
          <StatCard label="Pending" value={0} icon="⏳" color="#f59e0b" />
          <StatCard label="Completed" value={0} icon="✅" color="#10b981" />
        </div>
        <GlassCard className="p-16 text-center">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-gray-500 font-semibold text-lg">No exams assigned yet</p>
          <p className="text-gray-400 text-sm mt-2">Check back later or contact your administrator</p>
        </GlassCard>
      </PageShell>
    );
  }

  return (
    <PageShell title="My Exams" subtitle="View and attempt your assigned assessments">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Total" value={enhancedAssignments.length} icon="📋" color="#6366f1" />
        <StatCard label="Pending" value={pendingExams.length} icon="⏳" color="#f59e0b" />
        <StatCard label="Completed" value={completedExams.length} icon="✅" color="#10b981" />
      </div>

      {pendingExams.length > 0 && (
        <div className="mb-8">
          <h3 className="font-extrabold text-gray-700 text-sm uppercase tracking-wider mb-4">
            Active Exams ({pendingExams.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pendingExams.map(assignment => {
              const exam = assignment.examData;

              if (!exam) {
                return (
                  <GlassCard key={assignment.id} className="p-6">
                    <div className="flex flex-col h-full">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-2xl">
                          ❓
                        </div>
                        <Badge text={assignment.status} />
                      </div>
                      <h3 className="font-extrabold text-gray-800 text-lg mb-2">
                        Exam ID: {assignment.examId}
                      </h3>
                      <p className="text-sm text-red-500 mb-4">
                        Assessment template not found
                      </p>
                      <div className="mt-auto">
                        <Button variant="secondary" disabled className="w-full">
                          Unavailable
                        </Button>
                      </div>
                    </div>
                  </GlassCard>
                );
              }

              const buttonConfig = getButtonConfig(assignment);

              return (
                <GlassCard key={assignment.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col h-full">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl">
                        📋
                      </div>
                      <Badge text={assignment.status} />
                    </div>

                    <h3 className="font-extrabold text-gray-800 text-lg mb-2">
                      {exam.name}
                    </h3>

                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                      {exam.description || "No description available"}
                    </p>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Duration:</span>
                        <span className="font-semibold text-gray-700">{exam.timeLimit || 0} minutes</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Questions:</span>
                        <span className="font-semibold text-gray-700">{exam.totalQuestions || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Start Time:</span>
                        <span className="font-semibold text-gray-700">{formatDateTime(assignment.scheduledStart)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">End Time:</span>
                        <span className="font-semibold text-gray-700">{formatDateTime(assignment.scheduledEnd)}</span>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <Button
                        variant={buttonConfig.variant}
                        onClick={buttonConfig.onClick}
                        disabled={buttonConfig.disabled}
                        className="w-full"
                      >
                        {buttonConfig.text}
                      </Button>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </div>
      )}

      {completedExams.length > 0 && (
        <div>
          <h3 className="font-extrabold text-gray-700 text-sm uppercase tracking-wider mb-4">
            Completed Exams ({completedExams.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedExams.map(assignment => {
              const exam = assignment.examData;
              if (!exam) return null;

              return (
                <GlassCard key={assignment.id} className="p-6 opacity-75">
                  <div className="flex flex-col h-full">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-2xl">
                        ✅
                      </div>
                      <Badge text="completed" />
                    </div>

                    <h3 className="font-extrabold text-gray-700 text-lg mb-2">
                      {exam.name}
                    </h3>

                    <p className="text-sm text-gray-400 mb-4">
                      {exam.description}
                    </p>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Completed:</span>
                        <span className="text-gray-600">{formatDateTime(assignment.completedAt)}</span>
                      </div>
                      {assignment.maxScore && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Score:</span>
                          <span className="font-bold text-emerald-600">{assignment.maxScore}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-auto">
                      <Button variant="secondary" className="w-full">
                        View Results
                      </Button>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default MyExamsPage;